/*
 * One map layer per contract type.
 *
 * The Leasehold layer is oil and gas, drawn from the WI_* properties. Every other contract type a
 * project has (brine, solar, right of way, ...) is baked by GISService under its own prefix --
 * Brine_Composition, Brine_Owner, Brine_Is_Client_Owned, ... -- and listed in the tracts file's
 * top-level `contractLayers` manifest. Each gets its own layer, drawn exactly like the Leasehold
 * layer from its own fields; nothing is combined across types.
 *
 * The field names must match ContractLayerFields in LandHawk.Service/ShapeFileGeojsonData.cs.
 */

export interface IContractLayerManifestEntry {
    /** The agreement type's canonical name, e.g. 'Brine Lease'. The layer's name is contractLayerTitle. */
    agreementType: string;
    /** What the layer's properties are baked under, e.g. 'Brine' for Brine_Composition. */
    prefix: string;
}

/**
 * Every contract type that gets its own layer, in the order the map lists them: the agreement types
 * with a GisPrefix in LandHawk.DataAccess/ThirdPartyContractTypeMap.cs. A project's tracts file only
 * carries the types it has; this is the full list, for settings that name layers before any exist.
 */
export const CONTRACT_LAYER_TYPES: IContractLayerManifestEntry[] = [
    { agreementType: 'Brine Lease', prefix: 'Brine' },
    { agreementType: 'Solar Lease', prefix: 'Solar' },
    { agreementType: 'Right Of Way', prefix: 'RightOfWay' },
    { agreementType: 'Surface Waiver', prefix: 'SurfaceWaiver' },
    { agreementType: 'Battery Storage', prefix: 'BatteryStorage' },
    { agreementType: 'Carbon Capture System', prefix: 'CarbonCapture' },
];

/** The oil and gas Leasehold layer's name, at the top of its legend and in the GIS portal's list. */
export const LEASEHOLD_LAYER_TITLE = 'Leaseholds O&G';

/* The contract layers named as leaseholds, like the oil and gas layer and the tract panel's tabs. */
const LEASEHOLD_CONTRACT_LAYER_TITLES: Record<string, string> = { Brine: 'Leaseholds Brine' };

/**
 * A contract layer's name, at the top of its legend and in the GIS portal's list: Leaseholds Brine
 * for brine leases, otherwise its agreement type (Solar Lease, Right Of Way, ...).
 */
export const contractLayerTitle = (entry: IContractLayerManifestEntry): string =>
    LEASEHOLD_CONTRACT_LAYER_TITLES[entry.prefix] ?? entry.agreementType;

/** The three properties a contract layer is drawn from. */
export interface IContractLayerFields {
    composition: string;
    owner: string;
    clientOwned: string;
}

/** The oil and gas Leasehold layer. Its client flag predates the prefix scheme. */
export const OIL_AND_GAS_LAYER_FIELDS: IContractLayerFields = {
    composition: 'WI_Composition',
    owner: 'WI_Owner',
    clientOwned: 'Is_Client_Owned',
};

export const contractLayerFields = (prefix: string): IContractLayerFields => ({
    composition: `${prefix}_Composition`,
    owner: `${prefix}_Owner`,
    clientOwned: `${prefix}_Is_Client_Owned`,
});

/* A prefix ends up inside an Arcade expression ($feature.Brine_Composition), so anything that isn't
 * a plain identifier is refused rather than interpolated. */
const PREFIX_PATTERN = /^[A-Za-z][A-Za-z0-9]*$/;

/**
 * The contract layers a tracts file carries, in the order the map lists them. Empty for a file
 * enriched before contract layers existed, or one whose project has only oil and gas.
 */
export const readContractLayerManifest = (tractsData: unknown): IContractLayerManifestEntry[] => {
    const raw = (tractsData as { contractLayers?: unknown } | null | undefined)?.contractLayers;
    if (!Array.isArray(raw)) {
        return [];
    }

    const seen = new Set<string>();
    const entries: IContractLayerManifestEntry[] = [];
    raw.forEach((item: unknown) => {
        const entry = item as Partial<IContractLayerManifestEntry> | null | undefined;
        const prefix = typeof entry?.prefix === 'string' ? entry.prefix.trim() : '';
        const agreementType = typeof entry?.agreementType === 'string' ? entry.agreementType.trim() : '';
        if (!PREFIX_PATTERN.test(prefix) || seen.has(prefix)) {
            return;
        }
        seen.add(prefix);
        entries.push({ prefix, agreementType: agreementType || prefix });
    });

    return entries;
};

/**
 * The renderer's Arcade expression: the tract's effective composition, e.g.
 * '__CLIENT__ | AR Brine, LLC | __UNLEASED__'.
 *
 * Prefers the server-computed composition; when the file doesn't carry one (an oil and gas file
 * enriched before WI_Composition existed) it derives an equivalent token from the owner and client
 * flag. It references the composition field only when the file has it, because naming a field
 * the layer doesn't have raises in Arcade, and a raising expression paints nothing.
 * MUST stay in sync with the collection in collectLeaseholdValues.
 */
export const contractLayerValueExpression = (fields: IContractLayerFields, hasComposition: boolean): string => {
    const fromOwner = `
                if ($feature.${fields.clientOwned} == 1) { return '__CLIENT__'; }
                var wi = $feature.${fields.owner};
                if (IsEmpty(wi) || Trim(wi) == '' || Trim(wi) == 'Unleased') { return '__UNLEASED__'; }
                return Trim(wi);
            `;

    return hasComposition
        ? `
                var comp = $feature.${fields.composition};
                if (!IsEmpty(comp) && Trim(comp) != '') { return Trim(comp); }${fromOwner}`
        : fromOwner;
};

export interface ILeaseholdValues {
    /** The ten most frequent non-client owners, most frequent first. They get palette colors. */
    top10: string[];
    allValues: string[];
    /** Set when exactly one owner name ever appears on a client-owned tract: the legend names it. */
    clientOwnerName?: string;
    clientOwnerNames: Set<string>;
    /** Every distinct effective composition on the map, i.e. every value the renderer can return. */
    compositions: string[];
    /** Whether any feature carries the composition field (see contractLayerValueExpression). */
    hasRawComposition: boolean;
}

type TractProperties = Record<string, string | number | boolean | null | undefined>;

/**
 * Everything a contract layer's legend and renderer need from the tracts file: its compositions,
 * its most frequent owners, and which owners are the client's.
 */
export const collectLeaseholdValues = (
    features: Array<{ properties?: TractProperties | null }> | undefined,
    fields: IContractLayerFields
): ILeaseholdValues => {
    // count frequency of each individual WI owner
    const ownerCounts = new Map<string, number>();
    const allRawValues = new Set<string>();
    // All distinct individual owner names that ever appear on a client-owned tract.
    // Used to (a) label the synthetic __CLIENT_OWNED__ legend entry, and
    // (b) keep these names out of the auto-assigned palette.
    const clientOwnerNames = new Set<string>();
    // Distinct composition band-token strings (server-computed source of truth:
    // e.g. "__CLIENT__ | Little Bear Resources, LLC | __UNLEASED__").
    // Drives the layer's client/other/unleased hatch.
    const compositionsSet = new Set<string>();
    // Whether the tracts file actually carries the server-computed composition field. When false
    // (older files), the renderer must NOT reference that field in Arcade — doing so raises a
    // field-not-found error and the tract fails to draw.
    let sawRawComposition = false;

    (features ?? []).forEach((feature) => {
        // Collect the EFFECTIVE composition BEFORE the client-owned early return below. Prefer the
        // server-computed composition; when it is absent (e.g. a tracts file enriched before the
        // field existed), derive an equivalent token from the always-present owner / client flag so
        // the tract still renders. Kept in sync with contractLayerValueExpression.
        const props: TractProperties = feature?.properties || {};
        const rawComp = props[fields.composition] as string | undefined;
        let effComp: string;
        if (rawComp && String(rawComp).trim() !== '') {
            effComp = String(rawComp).trim();
            sawRawComposition = true;
        } else if (props[fields.clientOwned] === 1 || props[fields.clientOwned] === '1') {
            effComp = '__CLIENT__';
        } else {
            const wiRaw = props[fields.owner] as string | undefined;
            effComp = !wiRaw || String(wiRaw).trim() === '' || String(wiRaw).trim() === 'Unleased' ? '__UNLEASED__' : String(wiRaw).trim();
        }
        compositionsSet.add(effComp);

        // Client-owned tracts are styled by the synthetic CLIENT_OWNED entry, not by their owner
        // string — exclude them from frequency / unique-value collection. The server emits the flag
        // as integer 1 (ArcGIS GeoJSONLayer drops boolean fields); `=== true` covers older GeoJSONs.
        const isClient = props[fields.clientOwned];
        if (isClient === 1 || isClient === true) {
            const wi = props[fields.owner] as string | undefined;
            if (wi && wi.trim() !== '') {
                wi.split('|')
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0)
                    .forEach((n) => clientOwnerNames.add(n));
            }
            return;
        }

        let value = props[fields.owner] as string | undefined;
        if (!value || value.trim() === '') {
            value = 'Unleased';
        }
        allRawValues.add(value);

        // split pipe-delimited to count individual owners
        value
            .split('|')
            .map((v: string) => v.trim())
            .forEach((owner: string) => {
                if (owner && owner !== 'Unleased') {
                    ownerCounts.set(owner, (ownerCounts.get(owner) || 0) + 1);
                }
            });
    });

    // sort by frequency descending, take top 10
    const sorted = Array.from(ownerCounts.entries()).sort((a, b) => b[1] - a[1]);
    const top10 = sorted.slice(0, 10).map(([name]) => name);

    // full unique values list: single owners first, then pipe combos
    const allValuesArray = Array.from(allRawValues);
    const withPipes = allValuesArray.filter((v) => v.includes('|'));
    const withoutPipes = allValuesArray.filter((v) => !v.includes('|'));

    // If a single owner is the only client on this project, show their name in the legend.
    // Otherwise (multiple client owners, e.g. 1421.1) fall back to a generic label.
    const clientOwnerName: string | undefined = clientOwnerNames.size === 1 ? Array.from(clientOwnerNames)[0] : undefined;

    return {
        top10,
        allValues: [...withoutPipes, ...withPipes],
        clientOwnerName,
        clientOwnerNames,
        compositions: Array.from(compositionsSet),
        hasRawComposition: sawRawComposition,
    };
};

/* A WI owner acreage attribute ({prefix}_Owner_Acres, e.g. Brine_Owner_Acres: JSON [{name, isClient, netAcres}],
 * baked by the enricher) as owner name -> net acres. Empty when the attribute is absent or unreadable. */
export const ownerNetAcres = (value: string | number | undefined): Map<string, number> => {
    const byOwner = new Map<string, number>();
    if (typeof value !== 'string' || value === '') {
        return byOwner;
    }
    try {
        const rows: unknown = JSON.parse(value);
        (Array.isArray(rows) ? rows : []).forEach((row: { name?: unknown; netAcres?: unknown }) => {
            if (row && typeof row.name === 'string' && typeof row.netAcres === 'number') {
                byOwner.set(row.name, row.netAcres);
            }
        });
    } catch {
        /* unreadable: no acreage, the headings show without it */
    }
    return byOwner;
};

/* Each ownership's category as the map colors the tract by it (Ownership_Categories: JSON [{id, category}],
 * baked by the enricher) as ownership id -> category: an ownership something holds reads Leased or Leased
 * to Others. Empty when the attribute is absent or unreadable. */
export const ownershipCategories = (value: string | number | undefined): Map<number, string> => {
    const byOwnership = new Map<number, string>();
    if (typeof value !== 'string' || value === '') {
        return byOwnership;
    }
    try {
        const rows: unknown = JSON.parse(value);
        (Array.isArray(rows) ? rows : []).forEach((row: { id?: unknown; category?: unknown }) => {
            if (row && typeof row.id === 'number' && typeof row.category === 'string') {
                byOwnership.set(row.id, row.category);
            }
        });
    } catch {
        /* unreadable: the cards fall back to each owner's own category */
    }
    return byOwnership;
};



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/ContractLayers.ts